import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  GridComponent,
  Inject,
  ColumnsDirective,
  ColumnDirective,
  Search,
  Page,
  Sort,
  Filter,
  ExcelExport,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { connect } from "react-redux";
import { Header } from "../../../components";
import { useNavigate } from "react-router-dom";
import axios from "../../../api";
import { Button } from "@material-ui/core";
import { encrypt } from "../../../utils/crypt";
import Swal from "sweetalert2";
import BackdropComponent from "../../../components/Backdrop";
import moment from "moment";

const searchOptions = {
  fields: ["nombre"],
  ignoreCase: true,
  operator: "contains",
  key: "",
};

const Periods = (props) => {
  const history = useNavigate();
  const { token, rawPermission, userId } = props;
  const isMounted = useRef(true);

  const permission = useMemo(() => (
    (rawPermission || [])
      .filter((data) => data?.modulosAcciones?.id_modulos === 13)
      .map((item) => item?.modulosAcciones?.id_acciones)
  ), [rawPermission]);

  const fileName = "periodos";

  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const editButton = (row) => {
    const action = () => {
      history(`/periods/edit/${encrypt(row.id)}`);
    };
    return (
      <Button
        onClick={action}
        style={{ backgroundColor: "#9d2d39", color: "white", borderRadius: '8px' }}
        disabled={!(permission || []).includes(3)}
      >
        Editar
      </Button>
    );
  };

  const deleteButton = (row) => {
    const modalDelete = (id) => {
      Swal.fire({
        text: "¿Está seguro que desea eliminar este periodo?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#9d2d39",
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.value) {
          sendDelete(id);
        }
      });
    };

    const sendDelete = async (id) => {
      setLoading(true);
      try {
        const response = await axios.delete(`/period/${id}`, {
          data: { id_usuarios: userId },
          headers: { "access-token": token },
        });
        if (response?.data?.periodDeleted?.success) {
          Swal.fire({ text: "Eliminado exitosamente.", icon: "success", showConfirmButton: false, timer: 2000 });
          getPeriods();
        } else {
          Swal.fire({ text: response?.data?.message || "Error al eliminar", icon: "error" });
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    return (
      <Button
        onClick={() => modalDelete(row.id)}
        disabled={!(permission || []).includes(4)}
        style={{ backgroundColor: "#9d2d39", color: "white", borderRadius: '8px' }}
      >
        Eliminar
      </Button>
    );
  };

  useEffect(() => {
    if (Array.isArray(permission) && permission.includes(1) && periods.length === 0) {
      getPeriods();
    }
  }, [permission, periods.length]);

  const columnsGrid = useMemo(() => [
    { field: "nombre", headerText: "Nombre", textAlign: "Center" },
    { 
      headerText: "Fecha inicio", 
      textAlign: "Center", 
      template: (row) => row?.fecha_inicio ? moment(row.fecha_inicio).format("DD/MM/YYYY HH:mm:ss") : "N/A" 
    },
    { 
      headerText: "Fecha fin", 
      textAlign: "Center", 
      template: (row) => row?.fecha_fin ? moment(row.fecha_fin).format("DD/MM/YYYY HH:mm:ss") : "N/A" 
    },
    { headerText: "Editar", textAlign: "Center", width: "110", template: editButton },
    { headerText: "Eliminar", textAlign: "Center", width: "110", template: deleteButton }
  ], [permission]);

  const getPeriods = async () => {
    try {
      const response = await axios.post(`/period/getPeriods`, {}, {
        headers: { "access-token": token },
      });
      if (isMounted.current && response?.data?.success) {
        setPeriods(response.data.periods || []);
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
      if (isMounted.current) setPeriods([]);
    }
  };

  const toolbarOpts = [
    "Search",
    { text: "Crear periodo", prefixIcon: "e-add", id: "add_period", align: "Left" },
    { text: "Exportar", prefixIcon: "e-excelexport", id: "export_excel", align: "Left" },
  ];

  const handleToolbarClick = (args) => {
    if (args.item.id === "add_period") {
      history(`/periods/create`);
    } else if (args.item.id === "export_excel") {
      dataToExcel(periods);
    }
  };

  const dataToExcel = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return Swal.fire("Aviso", "No hay datos para exportar", "info");
    }
    const arrayExcel = data.map((item) => ({
      Nombre: item?.nombre || "N/A",
      "Fecha inicio": item?.fecha_inicio ? moment(item.fecha_inicio).format("DD/MM/YYYY HH:mm:ss") : "N/A",
      "Fecha fin": item?.fecha_fin ? moment(item.fecha_fin).format("DD/MM/YYYY HH:mm:ss") : "N/A",
    }));
    const ws = XLSX.utils.json_to_sheet(arrayExcel);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }), fileName + ".xlsx");
  };

  if (!Array.isArray(permission) || permission.length === 0) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Parametrización" title="Periodos" />
        <div className="p-10 text-center">Cargando configuración...</div>
        <BackdropComponent loading={true} />
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      <Header category="Parametrización evaluación docente" title="Periodos" />
      <GridComponent
        dataSource={periods}
        width="auto"
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 12 }}
        searchSettings={searchOptions}
        toolbar={toolbarOpts}
        toolbarClick={handleToolbarClick}
        allowTextWrap={true}
        columns={columnsGrid}
      >
        <Inject services={[Search, Page, Sort, Filter, Toolbar, ExcelExport]} />
      </GridComponent>
      <BackdropComponent loading={loading} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  token: state.token,
  rawPermission: state.permission || [],
  userId: state.user?.id,
});

export default connect(mapStateToProps, null)(Periods);
